// Core imports
import { ToolSchema } from "@/lib/schemas";
import { canvasCreateTool, canvasEditTool } from "./canvasStreamingTool";
import { imageEditTool } from "./imageEditTool";
import { imageGenTool } from "./ImageGenTool";
import { webSearchTool } from "./webSearchTool";

export const TOOL_REGISTRY = {
  image_gen: {
    ...imageGenTool,
    category: "images",
    type: "function",
  },
  image_edit: {
    ...imageEditTool,
    category: "images",
    type: "function",
  },

  web_search: {
    ...webSearchTool,
    category: "web",
    name: "Web Search",
    description: "Search the web for information",
    icon: "search",
    type: "function",
  },

  // Canvas tools
  canvas_create: {
    ...canvasCreateTool,
    category: "canvas",
    type: "function",
  },

  canvas_edit: {
    ...canvasEditTool,
    category: "canvas",
    type: "function",
  },
};
// Validate tool configurations
// Note: BaseTool instances need to be converted to plain objects for validation
Object.entries(TOOL_REGISTRY).forEach(([name, tool]) => {
  try {
    // Convert BaseTool instance to plain object if needed
    // Check if tool has BaseTool-like structure (has id, name, description, etc. as own properties)
    const isBaseToolInstance = tool &&
      typeof tool === 'object' &&
      'id' in tool &&
      'name' in tool &&
      'definition' in tool &&
      'execute' in tool;

    const toolObject = isBaseToolInstance
      ? {
          id: tool.id,
          name: tool.name,
          description: tool.description,
          category: tool.category,
          icon: tool.icon,
          runInstructions: tool.runInstructions,
          type: tool.type,
          definition: tool.definition,
          execute: tool.execute,
        }
      : tool;
    ToolSchema.parse(toolObject);
  } catch (error) {
    // Only log validation errors in non-test environments to avoid test noise
    // In test environments, these errors are expected and don't affect functionality
    if (typeof process === 'undefined' || (process.env.NODE_ENV !== 'test' && !process.env.VITEST)) {
      console.error(`Invalid tool configuration for ${name}:`, error);
    }
  }
});
export const TOOL_FUNCTIONAL_STATUS = {
  take_screenshot: null,
  places_search: null,
  code_review: null,
  web_search: true,
  web_documentation: null,
  image_edit: true,
  youtube_get_transcript: true,
  youtube_analyze_video: true,
};
export const PREMIUM_TOOLS = [
  "places_search",
  "youtube_get_transcript",
  "youtube_analyze_video",
];
export const TOOL_RUN_INSTRUCTIONS = {
  web_search: webSearchTool.runInstructions,
  image_edit: true,
};
export const TOOL_CATEGORIES = {
  analysis: {
    name: "Analysis Tools",
    description: "Tools for analyzing content, code, and user sentiment",
    tools: [],
  },
  web: {
    name: "Web Tools",
    description: "Tools for web scraping and content processing",
    tools: ["web_search"],
  },
  canvas: {
    name: "Canvas Tools",
    description: "Tools for creating and managing collaborative canvas content",
    tools: ["canvas_create", "canvas_edit"],
  },
  ai_chat: {
    name: "AI Chat Tools",
    description: "Tools for AI chat completions and streaming",
    tools: [],
  },
  creative: {
    name: "Creative Tools",
    description: "Tools for generating creative content",
    tools: [],
  },
  images: {
    name: "Image Tools",
    description: "Tools for generating and processing images",
    tools: [
      "image_gen",
      "image_edit",
      "image_gen_midjourney",
      "image_gen_stable_diffusion",
      "image_gen_venice_uncensored",
      "image_gen_newreality_nsfw",
      "image_analysis",
    ],
  },
  knowledge: {
    name: "Knowledge Tools",
    description: "Tools for searching and generating recommendations",
    tools: [],
  },
  system: {
    name: "System Tools",
    description: "Tools for file and system operations",
    tools: [],
  },
  files: {
    name: "File Tools",
    description: "Tools for file operations",
    tools: ["file_search_contents", "file_analysis", "analyze_file_contents"],
  },
  legal: {
    name: "Legal Tools",
    description: "Tools for legal operations",
    tools: [],
  },
  data: {
    name: "Data Tools",
    description: "Tools for data visualization and analysis",
    tools: [],
  },
  "health-fitness": {
    name: "Health & Fitness Tools",
    description: "Tools for health, fitness, and wellness planning",
    tools: [],
  },
  vision: {
    name: "Vision Tools",
    description: "Tools for image and visual content analysis",
    tools: ["image_analysis"],
  },

  youtube: {
    name: "YouTube Tools",
    description: "Tools for fetching and analyzing YouTube video content",
    tools: ["youtube_get_transcript", "youtube_analyze_video"],
  },
};
//-----------------------------------
// Agent Config
//-----------------------------------

//-----------------------------------
// Tool Name Utilities
//-----------------------------------

/**
 * Get tool information by tool name
 * @param {string} toolName - The name of the tool
 * @returns {Object|null} - Tool information or null if not found
 */
export const getToolByName = (toolName) => {
  if (!toolName) return null;

  // Check in the tool registry
  if (TOOL_REGISTRY[toolName]) {
    return TOOL_REGISTRY[toolName];
  }

  // Try to match by function name in registry
  const matchingTool = Object.entries(TOOL_REGISTRY).find(
    ([_, tool]) => tool.type === "function" && tool.function?.name === toolName
  );

  if (matchingTool) {
    return matchingTool[1];
  }

  return null;
};

/**
 * Convert an array of tool names to their full OpenAI-compatible tool structures
 * @param {string[]} toolNames - Array of tool names
 * @returns {Object[]} - Array of tool structures for OpenAI
 */
export const toolNamesToStructures = (toolNames = []) => {
  if (!Array.isArray(toolNames) || toolNames.length === 0) return [];

  return toolNames.map((toolName) => {
    // Try to find the tool in the registry
    const tool = getToolByName(toolName);

    if (tool) {
      if (tool.type === "function") {
        return {
          type: "function",
          function: {
            name: tool.function.name,
            description: tool.function.description || tool.description,
            parameters: tool.function.parameters,
          },
        };
      } else {
        return { type: tool.type };
      }
    }

    // Fallback for unknown function tools
    return {
      type: "function",
      function: {
        name: toolName,
        description: `Function: ${toolName}`,
        parameters: {
          type: "object",
          properties: {},
          required: [],
        },
      },
    };
  });
};

/**
 * Extract tool names from tool structures
 * @param {Object[]} tools - Array of tool structures
 * @returns {string[]} - Array of tool names
 */
export const structuresToToolNames = (tools = []) => {
  if (!Array.isArray(tools)) return [];

  return tools
    .map((tool) => {
      if (tool.type === "function" && tool.function?.name) {
        return tool.function.name;
      }
      return tool.type;
    })
    .filter(Boolean);
};

export default {
  TOOL_REGISTRY,
  TOOL_RUN_INSTRUCTIONS,
  TOOL_CATEGORIES,
  getToolByName,
  toolNamesToStructures,
  structuresToToolNames,
};
